import { FC, useContext } from "react";
import { Text, Image, Box, Stack, Paper, Flex } from "@mantine/core";
import { TeamLogo } from "../../../../Components/TeamLogo";
import { useRequiredParams } from "../../../../hooks/useRequiredParams";
import { TeamName } from "../../../../Components/TeamName";
import { PlayerNumber } from "../../../../Components/PlayerNumber";
import { PlayerName } from "../../../../Components/PlayerName";
import { CenteredLoader } from "../../../../Components/CenteredLoader";
import { SectionTitle } from "../../../../Components/SectionTitle";
import { useTeam } from "../../../../hooks/useTeam.ts";
import { CompetitionContext } from "../../../../CompetitionContext.tsx";
import { useTeamGames } from "../../../../hooks/useTeamGames.ts";
import { GameList } from "../Widgets/GameList.tsx";
import { usePlayerListByTeam } from "../../../../hooks/usePlayerListByTeam.ts";
import { getContrastColor } from "../../../../utils/getContrastColor.ts";
import { CardContainer } from "../../../../Components/CardContainer.tsx";
import { ListRow } from "../../../../Components/ListRow.tsx";
import { useDocumentTitle } from "../../../../hooks/useDocumentTitle.ts";
import { useMetaDescription } from "../../../../hooks/useMetaDescription.ts";
import { getPresentationParamsForOrigin } from "../../../../getPresentationParamsForOrigin.ts";

export const TeamPage: FC = () => {
  const { teamId } = useRequiredParams(["teamId"]);
  const competitionId = useContext(CompetitionContext);
  const teamData = useTeam(teamId);

  const teamPlayersData = usePlayerListByTeam(teamId);
  const teamGames = useTeamGames(competitionId, teamId);

  const sponsor = teamData.data?.sponsor;
  const primaryColor = teamData.data?.primaryColor ?? "#000000";
  const textColor = getContrastColor(primaryColor);

  useDocumentTitle(teamData.data?.name);
  useMetaDescription(
    teamData.data?.name
      ? `${teamData.data.name} beim ${getPresentationParamsForOrigin().competitionName} – Kader, Spiele und Ergebnisse.`
      : undefined,
  );

  if (!teamData.isSuccess) {
    return <CenteredLoader />;
  }

  return (
    <Stack gap="md" mt="md">
      {/* Hero: Logo + Name */}
      <Paper
        radius="md"
        p="xl"
        style={{
          backgroundColor: primaryColor,
          overflow: "hidden",
        }}
      >
        <Stack align="center" gap="sm">
          <TeamLogo teamId={teamId} height={100} />
          <Text fw={700} size="xl" ta="center" c={textColor}>
            <TeamName teamId={teamId} />
          </Text>
        </Stack>
      </Paper>

      {/* Teamfoto */}
      {teamData.data.photo && (
        <Paper radius="md" style={{ overflow: "hidden" }}>
          <Image
            src={teamData.data.photo.filePath}
            fallbackSrc={`https://placehold.co/1600x900?text=${teamData.data.name}`}
            alt="Teamfoto"
          />
        </Paper>
      )}

      {/* Sponsor */}
      {sponsor && (
        <CardContainer>
          <Stack align="center" gap="xs">
            <Text size="xs" c="dimmed">
              Präsentiert von
            </Text>
            {sponsor.url ? (
              <a href={sponsor.url} target="_blank" rel="noopener noreferrer">
                <Image
                  h={40}
                  w="auto"
                  fit="contain"
                  src={sponsor.logoFilePath}
                  fallbackSrc={`https://placehold.co/200x60?text=${sponsor.name}`}
                  alt={sponsor.name}
                />
              </a>
            ) : (
              <Image
                h={40}
                w="auto"
                fit="contain"
                src={sponsor.logoFilePath}
                fallbackSrc={`https://placehold.co/200x60?text=${sponsor.name}`}
                alt={sponsor.name}
              />
            )}
          </Stack>
        </CardContainer>
      )}

      {/* Spiele */}
      <Box mt="sm">
        <SectionTitle color={primaryColor}>Spiele</SectionTitle>
      </Box>
      {teamGames.isSuccess ? (
        <CardContainer p="xs">
          <GameList games={teamGames.data.games} />
        </CardContainer>
      ) : (
        <CenteredLoader />
      )}

      {/* Spieler */}
      {teamPlayersData.isSuccess && teamPlayersData.data.length > 0 && (
        <>
          <Box mt="sm">
            <SectionTitle color={primaryColor}>Kader</SectionTitle>
          </Box>
          <CardContainer p="sm">
            <Stack gap={0}>
              {teamPlayersData.data.map((p, index) => (
                <ListRow
                  key={p.id}
                  isFirst={index === 0}
                  isLast={index === teamPlayersData.data.length - 1}
                  gap="sm"
                >
                  <Flex
                    w={28}
                    h={28}
                    align="center"
                    justify="center"
                    style={{
                      borderRadius: 6,
                      backgroundColor: primaryColor,
                      color: textColor,
                      flexShrink: 0,
                    }}
                  >
                    <Text fw={700} size="xs">
                      <PlayerNumber playerId={p.id} />
                    </Text>
                  </Flex>
                  <Text size="sm">
                    <PlayerName
                      variant={"lastname, firstname"}
                      playerId={p.id}
                    />
                  </Text>
                </ListRow>
              ))}
            </Stack>
          </CardContainer>
        </>
      )}

      {/* Staff */}
      {teamData.data.staff && (
        <>
          <Box mt="sm">
            <SectionTitle color={primaryColor}>Trainer & Betreuer</SectionTitle>
          </Box>
          <CardContainer p="sm">
            <Text size="sm">{teamData.data.staff}</Text>
          </CardContainer>
        </>
      )}
    </Stack>
  );
};
